import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { tagsService } from "@/services/tags.service";
import type { FeedPost } from "@/hooks/useRecentStashes";

/** Every post carrying any of these tags, for narrowing a list of ids by tag. */
export function useTagPostIds(selectedTags: string[], enabled = true) {
  const selectedTagsKey = useMemo(() => [...selectedTags].sort().join(","), [selectedTags]);

  return useQuery({
    queryKey: ["tag-post-ids", selectedTagsKey],
    enabled: enabled && selectedTags.length > 0,
    queryFn: async () => {
      const postIdSets = await Promise.all(
        selectedTags.map(tagId => tagsService.getAllPostIdsForTag(tagId)),
      );
      const all = new Set<string>();
      for (const ids of postIdSets) {
        for (const id of ids) all.add(id);
      }
      return all;
    },
    staleTime: 30_000,
  });
}

/**
 * When both search and tags are active, fetches all post IDs for the selected tags
 * and provides a filter function to intersect search results with tag membership.
 */
export function useSearchTagIntersection(
  isSearchActive: boolean,
  selectedTags: string[],
) {
  const isTagFilterActive = selectedTags.length > 0;
  const { data: tagPostIds = new Set<string>(), isLoading } = useTagPostIds(selectedTags, isSearchActive);

  const filterByTags = useMemo(() => {
    if (!isSearchActive || !isTagFilterActive) return null;
    if (isLoading) return () => [] as FeedPost[];
    return (posts: FeedPost[]) =>
      posts.filter(p => tagPostIds.has(p.id) || (p.stasht_id && tagPostIds.has(p.stasht_id)));
  }, [isSearchActive, isTagFilterActive, isLoading, tagPostIds]);

  return { filterByTags, isLoading: isSearchActive && isTagFilterActive && isLoading };
}
